import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import cloneDeep from 'lodash/cloneDeep';
import Immerable from '@naruto/naruto-models/src/types/GeneralImmer';

dayjs.extend(isBetween);
class TimeParser extends Immerable {
  /**
   * 名稱 : ceToRoc
   * 功能 : 西元轉民國
   */
  ceToRoc = (dayString: string): number[] => {
    const year = Number(dayjs(dayString).format('YYYY')) - 1911;
    const month = Number(dayjs(dayString).format('MM'));
    const day = Number(dayjs(dayString).format('DD'));
    return [year, month, day];
  };

  /**
   * 名稱 : rocToCe
   * 功能 : 民國轉西元
   */
  rocToCe = (rocNumberArray: number[]): string => {
    if (rocNumberArray.length === 3) {
      return dayjs(`${1911 + rocNumberArray[0]}/${rocNumberArray[1]}/${rocNumberArray[2]}`).format('YYYY/MM/DD');
    }
    return 'unknown date';
  };

  /**
   * 名稱 : getWorkDayStart
   * 功能 : 取得工作日
   */
  getWorkDayStart = (
    startDate = dayjs().startOf('d').format('YYYY/MM/DD'),
    limitDay = 7,
    notWorkDays = false
  ): string => {
    const ignoreWorkingDay = ['1911/01/01', '1911/02/28', '1911/05/01', '1911/10/10'];

    const startTime = dayjs(startDate).unix() * 1000;
    const T = 24 * 60 * 60 * 1000;
    const endTime = startTime + limitDay * T;
    if (limitDay > 0) {
      let holidays = 0;
      for (let i = startTime + T; i <= endTime; i += T) {
        const date = dayjs(i).startOf('d');
        // 此處爲節假日邏輯
        if ((!notWorkDays && Number(date.format('d')) === 0) || Number(date.format('d')) === 6) {
          holidays += 1;
        }
        // 判斷日期是否在節假日數組中
        if (!notWorkDays && ignoreWorkingDay.includes(date.set('year', 1911).format('YYYY/MM/DD'))) {
          holidays += 1;
        }
      }
      return this.getWorkDayStart(dayjs(endTime).startOf('d').format('YYYY/MM/DD'), holidays);
    }
    return dayjs(startTime).format('YYYY/MM/DD');
  };

  /**
   * 名稱 : checkIsOverdue
   * 功能 : 確認該日期是否過期
   */
  checkIsOverdue = (expectDate: string, actualDate?: string | string[]): boolean => {
    if (actualDate) {
      if (!Array.isArray(actualDate)) {
        return dayjs(actualDate).startOf('d').isAfter(dayjs(expectDate).startOf('d'));
      }
      if (Array.isArray(actualDate)) {
        if (actualDate.length > 0) {
          const sortDate = cloneDeep(actualDate).sort((dateStringA, dateStringB) => {
            const dateA = dayjs(dateStringA);
            const dateB = dayjs(dateStringB);
            if (dateA.isAfter(dateB)) {
              return 1;
            }
            if (dateA.isBefore(dateB)) {
              return -1;
            }
            return 0;
          });
          return dayjs(sortDate[0]).startOf('d').isAfter(dayjs(expectDate).startOf('d'));
        }
        return true;
      }
    }
    // 同一天不算逾期
    return dayjs().add(-1, 'd').isAfter(dayjs(expectDate));
  };

  /**
   * 名稱 : checkDateRangeIsDuplicate
   * 功能 : 確認日期區間是否重疊
   */
  checkDateRangeIsDuplicate = (dateRange: string[], comparedDateRange: string[]): boolean => {
    const [startDate, endDate] = dateRange;
    const [comparedStartDate, comparedEndDate] = comparedDateRange;

    /* 新始日不可與原先迄日重疊 */
    const isStartDateDuplicate = dayjs(startDate).isSame(comparedStartDate) || dayjs(startDate).isSame(comparedEndDate);
    /* 新迄日不可與原先始日重疊 */
    const isEndDateDuplicate = dayjs(endDate).isSame(comparedStartDate) || dayjs(endDate).isSame(comparedEndDate);

    const isDateRangeDuplicate =
      /* 新始日早於原先始日時，新迄日未早於原先始日則重疊 */
      (dayjs(startDate).isBefore(comparedStartDate) && !dayjs(endDate).isBefore(comparedStartDate)) ||
      /* 新迄日晚於原先迄日時，新始日未晚於原先迄日則重疊， */
      (dayjs(endDate).isAfter(comparedEndDate) && !dayjs(startDate).isAfter(comparedEndDate)) ||
      /* 新始日或新迄日位於原先期間內，則重疊， */
      dayjs(startDate).isBetween(comparedStartDate, comparedEndDate) ||
      dayjs(endDate).isBetween(comparedStartDate, comparedEndDate);

    return isStartDateDuplicate || isEndDateDuplicate || isDateRangeDuplicate;
  };

  /**
   * 名稱 : 取得星期
   * 功能 : 取得某星期加幾日後的星期
   */
  getAddedWeekday = (weekday: string, daysToAdd: number): string => {
    const weekMap = {
      Mon: '2025-09-01', // 星期一
      Tue: '2025-09-02',
      Wed: '2025-09-03',
      Thu: '2025-09-04',
      Fri: '2025-09-05',
      Sat: '2025-09-06',
      Sun: '2025-09-07'
    };
    const baseDate = dayjs(weekMap[weekday]);
    const newDate = baseDate.add(daysToAdd, 'day');
    return newDate.format('ddd'); // 輸出 'Mon' | 'Tue' | ...
  };
}

export default TimeParser;
